import React from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';

import { formatAmountWithCode, formatAmount } from '../../../../data/billing';
import { currencySymbol } from '../../../../lib/currency';
import type { PixDetails } from '../../../../data/pix-details';
import { SPEI } from '../../spei.tokens';
import { CopyField, CopyIconButton } from './CopyField';
import { QaFinalizeControl } from './QaFinalizeControl';
import { PixLogo } from './PixLogo';
import {
  Root,
  Scroll,
  LogoRow,
  Title,
  Lead,
  AmountBlock,
  AmountLabels,
  AmountLabel,
  AmountValue,
  Panel,
  Notice,
  NoticeIcon,
  NoticeText,
  Expiry,
  StepsTitle,
  StepsList,
  StepRow,
  StepIndex,
  StepText,
  Footer,
  ContinueButton,
  ContinueLabel,
  QrBlock,
  QrPlate,
  Disclosure,
  DisclosureSummary,
  DisclosureLabel,
} from './PixInstructions.styles';

export type PixInstructionsProps = {
  details: PixDetails;
  /** Amount due today from the wizard's billing. Pix details carry no amount, so this is the figure shown. */
  fallbackAmount: number;
  /** Currency of {@link fallbackAmount}. */
  fallbackCurrency: string;
  /** The buyer confirms they have made the payment. */
  onContinue: () => void;
  /** True while that confirmation is being registered with the backend. */
  continuing?: boolean;
  /** TEST-ONLY settlement back-door URL; see `QaFinalizeControl`. `null`/absent in production. */
  qaFinalizeUrl?: string | null;
  /**
   * Phone layout: the copy-and-paste code leads (the buyer is usually paying on this same
   * device, where a QR cannot be scanned), and the QR folds away for a second device.
   */
  compact?: boolean;
};

/** Rendered QR edge in px: large enough for a phone camera at arm's length, small enough for the 390px pane. */
const QR_SIZE = 184;

/** How many characters of the code stay visible at each end of the abbreviated display. */
const HEAD_CHARS = 24;
const TAIL_CHARS = 8;

/**
 * A BR Code runs to well over a hundred characters, which would wrap into an unreadable block.
 * The row shows its head and tail joined by an ellipsis; the clipboard still gets the FULL code
 * (see `CopyField.display`), since a truncated code is rejected by every bank.
 */
export function abbreviatePixCode(code: string): string {
  if (code.length <= HEAD_CHARS + TAIL_CHARS + 1) return code;
  return `${code.slice(0, HEAD_CHARS)}…${code.slice(-TAIL_CHARS)}`;
}

const ClockGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke={SPEI.noticeIcon} strokeWidth="1.6" />
    <path d="M12 7.5V12l3 2" stroke={SPEI.noticeIcon} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The Pix payment screen: the amount, the QR to scan and the "copia e cola" code to paste.
 *
 * The code has no expiry shown on purpose: the backend mints a fresh invoice on each Continue, so
 * a code that stopped working is fixed by going back, not by a countdown.
 */
export const PixInstructions = ({
  details,
  fallbackAmount,
  fallbackCurrency,
  onContinue,
  continuing = false,
  qaFinalizeUrl,
  compact = false,
}: PixInstructionsProps): React.ReactElement => {
  const { t } = useTranslation();

  // Pix details carry no amount, so the wizard's own total is the figure (never `invoice.price`).
  const amountText = `${currencySymbol(fallbackCurrency)}${formatAmountWithCode(fallbackAmount, fallbackCurrency)}`;

  const qr = (
    <QrBlock>
      <QrPlate>
        <QRCodeSVG
          value={details.code}
          size={QR_SIZE}
          level="M"
          marginSize={0}
          title={t('Pix QR code')}
          role="img"
          aria-label={t('Pix QR code')}
        />
      </QrPlate>
    </QrBlock>
  );

  const codeField = (
    <Panel>
      <CopyField label={t('Pix code')} value={details.code} display={abbreviatePixCode(details.code)} gaId="pix-code" />
    </Panel>
  );

  return (
    <Root>
      <Scroll>
        <LogoRow>
          <PixLogo height={18} />
        </LogoRow>
        <Title text="Pay with Pix" tag="paragraph" />
        <Lead
          text="Scan the code with your bank app, or copy the Pix code and paste it under Pix Copia e Cola. Your brand is reserved while we wait for the payment."
          tag="paragraph"
        />

        {/* Same headline amount as SPEI; the clipboard gets the bare number. */}
        <AmountBlock>
          <AmountLabels>
            <AmountLabel text="Amount to transfer" tag="span" />
            <AmountValue text={amountText} tag="span" />
          </AmountLabels>
          <CopyIconButton label={t('Amount')} value={formatAmount(fallbackAmount)} gaId="amount" />
        </AmountBlock>

        {compact ? (
          <>
            {codeField}
            <StepsTitle text="How to pay" tag="paragraph" />
            <StepsList>
              <StepRow>
                <StepIndex>1</StepIndex>
                <StepText text="Copy the Pix code above." tag="span" />
              </StepRow>
              <StepRow>
                <StepIndex>2</StepIndex>
                <StepText text="Open your bank app and choose Pix." tag="span" />
              </StepRow>
              <StepRow>
                <StepIndex>3</StepIndex>
                <StepText text="Choose Pix Copia e Cola and paste the code." tag="span" />
              </StepRow>
            </StepsList>
            <Disclosure>
              <DisclosureSummary>
                <DisclosureLabel text="Paying from another device?" tag="span" />
              </DisclosureSummary>
              {qr}
            </Disclosure>
          </>
        ) : (
          <>
            {qr}
            {codeField}
          </>
        )}

        <Notice>
          <NoticeIcon>{ClockGlyph}</NoticeIcon>
          <NoticeText
            text="Pix payments are confirmed within seconds. We'll email you the moment yours lands."
            tag="paragraph"
          />
        </Notice>

        <Expiry text="If the code no longer works, go back to get a new one." tag="paragraph" />
      </Scroll>

      <Footer>
        <ContinueButton
          type="button"
          data-ga-id="cfg-mx-pix-done"
          onClick={() => {
            // Self-gate: the acknowledge is a real backend transition, so a double click must not send two.
            if (continuing) return;
            onContinue();
          }}
          disabled={continuing}
        >
          <ContinueLabel text={continuing ? t('Confirming…') : t("I've made the payment")} tag="span" />
        </ContinueButton>
      </Footer>

      {qaFinalizeUrl ? <QaFinalizeControl url={qaFinalizeUrl} /> : null}
    </Root>
  );
};

export default PixInstructions;
